import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, ArrowRight } from "lucide-react";
import Logo from "../Logo";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useFavorites } from "../../context/FavoritesContext";
import { useLanguage } from "../../context/LanguageContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";

const LINKS = [
  { label: "Sérums", to: "/shop" },
  { label: "Le Protocole", to: "/#routine" },
  { label: "Ingrédients", to: "/#ingredients" },
  { label: "Résultats", to: "/#results" },
  { label: "La Maison", to: "/apropos" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout, isAdmin } = useAuth();
  const { itemCount, openCart, bump } = useCart();
  const { lang, setLang, t, isRTL } = useLanguage();
  const fav = useFavorites();
  const navigate = useNavigate();
  const location = useLocation();
  const [bumped, setBumped] = useState(false);

  const navLinks = [
    { label: t("nav_serums"), to: "/shop" },
    { label: t("nav_protocol"), to: "/#routine" },
    { label: t("nav_ingredients"), to: "/#ingredients" },
    { label: t("nav_results"), to: "/#results" },
    { label: t("nav_maison"), to: "/apropos" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (bump > 0) {
      setBumped(true);
      const t = setTimeout(() => setBumped(false), 350);
      return () => clearTimeout(t);
    }
  }, [bump]);

  const isHome = location.pathname === "/";
  const transparentAtTop = isHome && !scrolled && !searchOpen;
  const solid = scrolled || searchOpen || !isHome;

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only p-2 bg-black text-white z-[99]">Aller au contenu</a>
      
      {/* Announcement Bar */}
      <div className={`text-[#111111] py-1.5 px-4 text-center text-[10px] sm:text-[11px] font-body tracking-[0.2em] uppercase transition-all duration-500 ${
        transparentAtTop 
          ? "bg-[#FAF8F5]/80 backdrop-blur-sm border-b border-[#EAE8E4]/40 text-[#444444]" 
          : "bg-[#111111] text-[#EAE8E4]"
      }`}>
        <span>{t("announcement")}</span>
      </div>

      <header
        data-testid="navbar"
        className="sticky top-0 left-0 right-0 z-50 transition-all duration-500 ease-out"
        style={{
          background: transparentAtTop ? "rgba(250, 248, 245, 0.45)" : "rgba(255, 255, 255, 0.94)",
          backdropFilter: transparentAtTop ? "blur(8px)" : "blur(24px)",
          WebkitBackdropFilter: transparentAtTop ? "blur(8px)" : "blur(24px)",
          borderBottom: transparentAtTop ? "1px solid rgba(234, 232, 228, 0.3)" : "1px solid rgba(234, 232, 228, 0.8)",
          boxShadow: solid && scrolled ? "0 4px 30px rgba(0, 0, 0, 0.04)" : "none",
        }}
      >
        <nav className="container-queen flex items-center justify-between" style={{ height: 68 }}>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 text-[#111111] hover:opacity-70 transition-opacity"
            aria-label="Ouvrir le menu"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>

          {/* Logo */}
          <div className="flex items-center">
            <Logo height={32} />
          </div>

          {/* Desktop Links */}
          <div className="hidden lg:flex items-center gap-9">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="font-body text-[#111111] hover:text-[#666666] transition-colors relative py-1 text-[13px] tracking-[0.06em] uppercase font-normal"
                data-testid={`nav-${l.label.toLowerCase()}`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          {/* Right Action Icons & Language Selector */}
          <div className="flex items-center gap-1 sm:gap-3">
            {/* 3-Language Selector Pill */}
            <div className="flex items-center rounded-full bg-black/5 p-0.5 border border-[#EAE8E4] text-[11px] font-body mr-1">
              <button
                type="button"
                onClick={() => setLang("fr")}
                className={`px-2 py-0.5 rounded-full transition-all text-[10px] sm:text-[11px] ${
                  lang === "fr" ? "bg-white text-black font-semibold shadow-sm" : "text-[#666666] hover:text-black"
                }`}
                title="Français"
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("ar")}
                className={`px-2 py-0.5 rounded-full transition-all text-[10px] sm:text-[11px] ${
                  lang === "ar" ? "bg-white text-black font-semibold shadow-sm" : "text-[#666666] hover:text-black"
                }`}
                title="العربية"
              >
                عربي
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`px-2 py-0.5 rounded-full transition-all text-[10px] sm:text-[11px] ${
                  lang === "en" ? "bg-white text-black font-semibold shadow-sm" : "text-[#666666] hover:text-black"
                }`}
                title="English"
              >
                EN
              </button>
            </div>

            {/* Quick Admin CRM link if logged in as admin */}
            {isAdmin && (
              <Link
                to="/admin"
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#111111] text-white text-[11px] font-body tracking-[0.08em] uppercase hover:bg-[#333333] transition-colors"
                title="Tableau de bord CRM"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span>CRM</span>
              </Link>
            )}

            <button
              onClick={() => setSearchOpen((s) => !s)}
              className="p-2 text-[#111111] hover:opacity-60 transition-opacity"
              aria-label="Rechercher"
              data-testid="search-toggle"
            >
              <Search size={20} strokeWidth={1.5} />
            </button>

            <Link
              to="/favorites"
              className="relative p-2 text-[#111111] hover:opacity-60 transition-opacity hidden sm:flex"
              aria-label="Mes favoris"
              data-testid="favorites-link"
            >
              <Heart size={20} strokeWidth={1.5} />
              {fav.count > 0 && (
                <span className="absolute top-1 right-1 bg-[#111111] text-white rounded-full text-[9px] w-4 h-4 grid place-items-center font-medium">
                  {fav.count}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 text-[#111111] hover:opacity-60 transition-opacity" aria-label="Compte client">
                    <User size={20} strokeWidth={1.5} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-white border border-[#EAE8E4] rounded-2xl shadow-xl p-2">
                  <DropdownMenuLabel className="font-body text-xs text-[#666666] px-3 py-2">
                    {user.full_name || user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[#EAE8E4]" />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="px-3 py-2 text-sm text-[#111111] hover:bg-[#F8F8F6] rounded-xl cursor-pointer">
                      Mon Profil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="px-3 py-2 text-sm text-[#111111] hover:bg-[#F8F8F6] rounded-xl cursor-pointer">
                      Mes Commandes
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="px-3 py-2 text-sm font-medium text-[#111111] hover:bg-[#F8F8F6] rounded-xl cursor-pointer">
                        Administration
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#EAE8E4]" />
                  <DropdownMenuItem
                    onClick={logout}
                    className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl cursor-pointer"
                  >
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link
                to="/login"
                className="p-2 text-[#111111] hover:opacity-60 transition-opacity"
                aria-label="Connexion"
                data-testid="account-link"
              >
                <User size={20} strokeWidth={1.5} />
              </Link>
            )}

            {/* Cart Button */}
            <button
              onClick={openCart}
              className={`relative p-2 text-[#111111] hover:opacity-60 transition-transform ${bumped ? "scale-110" : ""}`}
              aria-label="Panier d'achats"
              data-testid="cart-button"
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {itemCount > 0 && (
                <span className="absolute top-1 right-1 bg-[#111111] text-white rounded-full text-[9px] w-4 h-4 grid place-items-center font-medium">
                  {itemCount}
                </span>
              )}
            </button>
          </div>
        </nav>

        {/* Search Bar Drawer */}
        {searchOpen && (
          <div className="border-t border-[#EAE8E4] bg-white py-4 px-6 animate-in slide-in-from-top-2">
            <form onSubmit={submitSearch} className="max-w-xl mx-auto flex items-center gap-3">
              <Search size={18} className="text-[#666666]" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un sérum, un actif, un protocole..."
                className="flex-1 bg-transparent border-none text-[#111111] placeholder-[#999999] text-sm focus:outline-none font-body"
                autoFocus
              />
              <button type="submit" className="text-xs uppercase tracking-wider text-[#111111] font-medium hover:opacity-60">
                Rechercher
              </button>
              <button type="button" onClick={() => setSearchOpen(false)} className="text-[#666666] hover:text-[#111111]">
                <X size={18} />
              </button>
            </form>
          </div>
        )}

        {/* Mobile Navigation Drawer */}
        {mobileOpen && (
          <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm lg:hidden flex">
            <div className="w-[300px] bg-white h-full p-6 flex flex-col justify-between shadow-2xl">
              <div>
                <div className="flex items-center justify-between pb-6 border-b border-[#EAE8E4]">
                  <Logo height={28} />
                  <button onClick={() => setMobileOpen(false)} className="p-1 text-[#111111]">
                    <X size={20} />
                  </button>
                </div>
                <div className="flex flex-col gap-3 mt-6">
                  {/* Language Selector in Mobile Drawer */}
                  <div className="flex items-center gap-2 pb-4 mb-2 border-b border-[#EAE8E4]">
                    <span className="text-xs font-body text-[#888888]">Langue:</span>
                    <button
                      type="button"
                      onClick={() => setLang("fr")}
                      className={`px-3 py-1 text-xs rounded-full ${lang === "fr" ? "bg-black text-white font-medium" : "bg-[#F4F1EA] text-black"}`}
                    >
                      FR
                    </button>
                    <button
                      type="button"
                      onClick={() => setLang("ar")}
                      className={`px-3 py-1 text-xs rounded-full ${lang === "ar" ? "bg-black text-white font-medium" : "bg-[#F4F1EA] text-black"}`}
                    >
                      العربية
                    </button>
                    <button
                      type="button"
                      onClick={() => setLang("en")}
                      className={`px-3 py-1 text-xs rounded-full ${lang === "en" ? "bg-black text-white font-medium" : "bg-[#F4F1EA] text-black"}`}
                    >
                      EN
                    </button>
                  </div>

                  {navLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className="text-lg font-display text-[#111111] hover:text-[#666666] py-2 border-b border-[#F8F8F6]"
                    >
                      {l.label}
                    </Link>
                  ))}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="text-base font-body text-emerald-700 font-semibold py-2 flex items-center gap-2"
                    >
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                      {t("nav_admin")} (CRM)
                    </Link>
                  )}
                  <Link
                    to="/favorites"
                    onClick={() => setMobileOpen(false)}
                    className="text-base font-body text-[#111111] py-2"
                  >
                    {t("nav_cart")} ({fav.count})
                  </Link>
                </div>
              </div>

              <div className="pt-6 border-t border-[#EAE8E4]">
                <p className="text-xs text-[#666666] uppercase tracking-wider mb-2">Service Client</p>
                <p className="text-sm text-[#111111] font-medium">+213 (0) 550 00 00 00</p>
                <p className="text-xs text-[#999999] mt-4">The Queen — Haute Dermatologie</p>
              </div>
            </div>
            <div className="flex-1" onClick={() => setMobileOpen(false)} />
          </div>
        )}
      </header>
    </>
  );
}
